import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Container = styled.div`
  display: flex;
  padding: 10vh 0;
  min-height: 75vh;
  background: transparent;
  color: white;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(74, 144, 226, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(144, 19, 254, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 2.5rem);
  margin-bottom: 2.5rem;
  text-align: center;
  font-weight: 800;
  font-family: "Outfit", "Inter", sans-serif;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #667eea, #14cda8);
    border-radius: 2px;
  }
`;

const TextSection = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(1.05rem, 2vw, 1.2rem);
  line-height: 1.9;
  text-align: center;
  max-width: 900px;
  margin-bottom: 3.5rem;
  font-weight: 300;
  font-family: "Inter", sans-serif;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: 1000px;
`;

const HighlightBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px) saturate(180%);
  border-radius: 24px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.03);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;

  /* Premium "Machined Glass" Inner Border */
  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 23px;
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.15);
    pointer-events: none;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4), 0 0 40px rgba(74, 144, 226, 0.1);
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(74, 144, 226, 0.3);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: center;
    border-radius: 20px;
  }
`;

const HighlightTitle = styled.h3`
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  margin-bottom: ${props => props.expanded ? '1rem' : '0'};
  font-weight: 600;
  font-family: "Outfit", "Inter", sans-serif;
  color: rgba(255, 255, 255, 0.95);
  transition: margin-bottom 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HighlightContent = styled.p`
  font-size: clamp(0.95rem, 2vw, 1.05rem);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  font-family: "Inter", sans-serif;
  animation: fadeIn 0.4s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Mission = () => {
  const [activeHighlight, setActiveHighlight] = useState(null);

  const highlights = [
    {
      title: "Strategic Planning & Execution",
      content: "Aligning compliance and risk-management insights with core business objectives through careful planning and decisive action."
    },
    {
      title: "Data-Driven Decision Making",
      content: "Leveraging BI Tools, SQL, and Python (Pandas, NumPy) to derive actionable intelligence that mitigates risk and drives growth."
    },
    {
      title: "Digital Transformation",
      content: "Orchestrating system upgrades, AI integration, and technological overhauls to streamline operations and ensure rigorous compliance."
    },
    {
      title: "Cross-Functional Leadership",
      content: "Championing Agile delivery while managing complex stakeholder structures to execute technology and operational strategies efficiently."
    },
    {
      title: "AI & Machine Learning",
      content: "Designing and deploying advanced models (Python, TensorFlow) to automate risk detection, uncover patterns, and solve complex business challenges."
    },
    {
      title: "IT Governance & Security",
      content: "Developing robust cybersecurity frameworks and system architectures that protect organizational data while ensuring strict regulatory compliance."
    }
  ];

  return (
    <Container id="mission" >
      <ContentWrapper>
        <Title>Professional Summary</Title>
        <TextSection>
          Highly analytical and results-focused technology leader with a Master’s in Computer Science and a strong background in strategic data analytics and commercial operations. Proven success in driving data-led initiatives for risk reduction, operational efficiency, and digital transformation. I combine deep technical fluency—spanning core AI/ML concepts, Python, SQL, and modern web development—with strategic planning to align compliance and risk-management insights with high-level business objectives.
        </TextSection>

        <GridContainer>
          {highlights.map((highlight, index) => (
            <HighlightBox
              key={index}
              onClick={() => setActiveHighlight(activeHighlight === index ? null : index)}
            >
              <HighlightTitle expanded={activeHighlight === index}>
                {highlight.title}
                <span style={{ fontSize: '0.8em', opacity: 0.5 }}>{activeHighlight === index ? '−' : '+'}</span>
              </HighlightTitle>
              {activeHighlight === index && (
                <HighlightContent>{highlight.content}</HighlightContent>
              )}
            </HighlightBox>
          ))}
        </GridContainer>

      </ContentWrapper>
    </Container>
  );
};

export default Mission;